import { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Shield, Clock, FileCheck, Users, ChevronRight, Star, CheckCircle, Home, Wrench, Droplets, Zap, PaintBucket, Thermometer, Menu, X, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_jatex-restoration/artifacts/qt4v4w6h_325d51de-5c76-46e5-b0a3-c049759d6ef2.jpeg";

// Icon mapping for dynamic content
const ICON_MAP = {
  Shield, Clock, FileCheck, Users, CheckCircle, Star, Home, Wrench, Award, Phone, MapPin, Zap, Droplets, PaintBucket, Thermometer
};

const getIcon = (iconName) => ICON_MAP[iconName] || Star;

export default function HomePage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', address: '', service_type: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchContent();
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Failed to load content');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.service_type) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...formData, source: 'contact_form' });
      toast.success('Thank you! We will contact you shortly.');
      setFormData({ name: '', phone: '', email: '', address: '', service_type: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please call us directly.');
    }
    setSubmitting(false);
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  // Show loading state
  if (loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <img src={LOGO} alt="JATEX" className="h-20 mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Get all service items for dropdown
  const allServices = content.services?.flatMap(cat => cat.items.map(item => item.title)) || [];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/95'}`} data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <div className="flex-shrink-0 bg-white p-2 shadow-lg -mb-8 relative z-10">
              <img src={LOGO} alt={content.company_name} className="h-16 lg:h-20 drop-shadow-md" data-testid="nav-logo" />
            </div>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-8">
              {['Services', 'Insurance Claims', 'Service Areas', 'Contact'].map((item) => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))} className="text-sm font-bold uppercase tracking-wider text-[#111827] hover:text-[#BF0D3E] transition-colors">
                  {item}
                </button>
              ))}
              <a href={`tel:${content.main_phone}`} className="flex items-center space-x-2 bg-[#BF0D3E] text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#8B0029] transition-all" data-testid="header-phone-btn">
                <Phone className="w-5 h-5" />
                <span>{content.main_phone}</span>
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-4">
              <a href={`tel:${content.main_phone}`} className="bg-[#BF0D3E] text-white p-3">
                <Phone className="w-5 h-5" />
              </a>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-[#111827]">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t shadow-xl">
            <nav className="px-4 py-6 space-y-4">
              {['Services', 'Insurance Claims', 'Service Areas', 'Contact'].map((item) => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))} className="block w-full text-left text-lg font-bold uppercase tracking-wide text-[#111827] py-2 hover:text-[#BF0D3E]">
                  {item}
                </button>
              ))}
              <a href={`tel:${content.main_phone}`} className="flex items-center justify-center space-x-2 bg-[#BF0D3E] text-white px-6 py-4 font-bold uppercase tracking-wider">
                <Phone className="w-5 h-5" />
                <span>{content.main_phone}</span>
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 lg:pt-40 pb-20 lg:pb-32 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-[#BF0D3E] font-bold uppercase tracking-[0.3em] mb-6 text-sm">
                Serving Houston & Surrounding Areas
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] uppercase tracking-tight leading-[1.1] mb-6">
                {content.hero_headline}
              </h1>
              <p className="text-lg lg:text-xl text-gray-600 mb-8 leading-relaxed">
                {content.hero_subtext}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button onClick={() => scrollToSection('contact')} className="h-14 px-8 bg-[#BF0D3E] hover:bg-[#8B0029] text-white font-bold uppercase tracking-wider text-base" data-testid="hero-cta-inspection">
                  Get a Free Roof Inspection
                </Button>
                <Button variant="outline" onClick={() => scrollToSection('contact')} className="h-14 px-8 border-2 border-[#111827] text-[#111827] font-bold uppercase tracking-wider text-base hover:bg-[#111827] hover:text-white" data-testid="hero-cta-consultation">
                  Schedule a Consultation
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center gap-6 text-sm">
                {[content.hero_badge1, content.hero_badge2, content.hero_badge3].map((badge, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-gray-600">
                    {idx === 0 && <Shield className="w-5 h-5 text-[#BF0D3E]" />}
                    {idx === 1 && <CheckCircle className="w-5 h-5 text-[#BF0D3E]" />}
                    {idx === 2 && <Clock className="w-5 h-5 text-[#BF0D3E]" />}
                    <span className="font-semibold uppercase tracking-wide">{badge}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:block relative">
              <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800" alt="Professional Roofing" className="w-full h-[500px] object-cover rounded-sm shadow-2xl" />
              <div className="absolute -bottom-6 -left-6 bg-[#BF0D3E] text-white p-6 shadow-xl">
                <p className="text-4xl font-bold">15+</p>
                <p className="text-sm uppercase tracking-wide">Years Experience</p>
              </div>
              <div className="absolute -top-6 -right-6 bg-[#1E3A5F] text-white p-6 shadow-xl">
                <p className="text-4xl font-bold">500+</p>
                <p className="text-sm uppercase tracking-wide">Roofs Completed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-32 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#BF0D3E] font-bold uppercase tracking-[0.3em] mb-4 text-sm">Why Choose Us</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] uppercase tracking-tight">
              {content.tagline}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {content.why_choose_cards?.map((card, idx) => {
              const IconComponent = getIcon(card.icon);
              return (
                <div key={idx} className="group p-8 bg-gray-50 border border-gray-100 hover:border-[#BF0D3E]/20 hover:shadow-xl transition-all duration-500" data-testid={`why-choose-card-${idx}`}>
                  <IconComponent className="w-12 h-12 text-[#BF0D3E] mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-bold uppercase tracking-wide text-[#111827] mb-3">{card.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{card.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 lg:py-32 bg-[#111827] relative" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <p className="text-[#007AFF] font-bold uppercase tracking-[0.3em] mb-4 text-sm">{content.company_name} Services</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight">
              Complete Restoration Solutions
            </h2>
          </div>

          {content.services?.map((category, catIdx) => (
            <div key={catIdx} className="mb-16">
              <h3 className="text-2xl font-bold text-white uppercase tracking-wide mb-8 flex items-center">
                <Home className="w-8 h-8 text-[#BF0D3E] mr-3" />
                {category.name}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.items?.map((service, idx) => (
                  <div key={idx} className="bg-white/5 border border-white/10 p-6 hover:bg-white/10 hover:border-[#BF0D3E]/30 transition-all duration-300" data-testid={`service-${catIdx}-${idx}`}>
                    <h4 className="text-lg font-bold text-white uppercase tracking-wide mb-2">{service.title}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{service.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Insurance Process */}
      <section id="insurance-claims" className="py-20 lg:py-32 bg-white" data-testid="insurance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#BF0D3E] font-bold uppercase tracking-[0.3em] mb-4 text-sm">Insurance Claim Process</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] uppercase tracking-tight">
              We Handle Everything
            </h2>
            <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
              From initial inspection to final installation, we guide you through every step of the insurance claim process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {content.process_steps?.map((step, idx) => (
              <div key={idx} className="relative" data-testid={`process-step-${idx}`}>
                <div className="bg-[#BF0D3E] text-white w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold uppercase tracking-wide text-[#111827] mb-3">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.desc}</p>
                {idx < (content.process_steps?.length - 1) && (
                  <ChevronRight className="hidden lg:block absolute top-6 -right-4 w-8 h-8 text-gray-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section id="service-areas" className="py-20 lg:py-32 bg-gray-50" data-testid="service-areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#BF0D3E] font-bold uppercase tracking-[0.3em] mb-4 text-sm">Service Areas</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] uppercase tracking-tight">
              Proudly Serving Texas
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.service_areas?.map((area, idx) => (
              <div key={idx} className="bg-white p-6 border border-gray-200 hover:border-[#BF0D3E]/30 hover:shadow-lg transition-all" data-testid={`service-area-${idx}`}>
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-5 h-5 text-[#BF0D3E]" />
                  <h3 className="text-lg font-bold uppercase tracking-wide text-[#111827]">{area.city}</h3>
                </div>
                <p className="text-gray-600 text-sm">{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Section */}
      <section className="py-20 lg:py-32 bg-[#BF0D3E] relative overflow-hidden" data-testid="emergency-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Clock className="w-16 h-16 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-6">
            {content.emergency_headline}
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            {content.emergency_text}
          </p>
          <a href={`tel:${content.emergency_phone}`} className="inline-flex items-center space-x-3 bg-white text-[#BF0D3E] px-10 py-5 font-bold uppercase tracking-wider hover:bg-gray-100 transition-all text-lg" data-testid="emergency-call-btn">
            <Phone className="w-6 h-6" />
            <span>Call Now: {content.emergency_phone}</span>
          </a>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-32 bg-white" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#BF0D3E] font-bold uppercase tracking-[0.3em] mb-4 text-sm">Customer Reviews</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] uppercase tracking-tight">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {content.testimonials?.map((testimonial, idx) => (
              <div key={idx} className="bg-gray-50 p-8 border border-gray-100" data-testid={`testimonial-${idx}`}>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div>
                  <p className="font-bold text-[#111827]">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-[#111827]" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={LOGO} alt={content.company_name} className="h-32 mx-auto mb-8" />
          <p className="text-xl text-gray-300 leading-relaxed italic">
            "{content.mission_text}"
          </p>
          <p className="text-[#BF0D3E] font-bold uppercase tracking-wider mt-6">
            — {content.owner1_name} & {content.owner2_name}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 lg:py-32 bg-gray-50" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-[#BF0D3E] font-bold uppercase tracking-[0.3em] mb-4 text-sm">Get In Touch</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] uppercase tracking-tight mb-6">
                Free Inspection
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Schedule your free roof inspection today. Our team will assess your property and provide a detailed report.
              </p>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#BF0D3E] flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wide text-gray-500">Phone</p>
                    <a href={`tel:${content.main_phone}`} className="text-xl font-bold text-[#111827] hover:text-[#BF0D3E]">{content.main_phone}</a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#BF0D3E] flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wide text-gray-500">Email</p>
                    <a href={`mailto:${content.email}`} className="text-xl font-bold text-[#111827] hover:text-[#BF0D3E]">{content.email}</a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-[#BF0D3E] flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wide text-gray-500">Location</p>
                    <p className="text-xl font-bold text-[#111827]">{content.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 shadow-xl border border-gray-100">
              <h3 className="text-2xl font-bold uppercase tracking-tight text-[#111827] mb-6">Request a Quote</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">Name *</label>
                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Your name" className="h-12" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">Phone *</label>
                    <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Your phone" className="h-12" data-testid="contact-phone-input" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">Email</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Your email" className="h-12" data-testid="contact-email-input" />
                </div>

                <div>
                  <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">Address</label>
                  <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Property address" className="h-12" data-testid="contact-address-input" />
                </div>

                <div>
                  <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">Service Needed *</label>
                  <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})}>
                    <SelectTrigger className="h-12" data-testid="contact-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="roof_inspection">Free Roof Inspection</SelectItem>
                      <SelectItem value="storm_damage">Storm Damage Repair</SelectItem>
                      <SelectItem value="roof_replacement">Roof Replacement</SelectItem>
                      <SelectItem value="emergency">Emergency Tarping</SelectItem>
                      <SelectItem value="insurance_claim">Insurance Claim Help</SelectItem>
                      <SelectItem value="other">Other Services</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">Message</label>
                  <Textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Tell us about your project" rows={4} data-testid="contact-message-input" />
                </div>

                <Button type="submit" disabled={submitting} className="w-full h-14 bg-[#BF0D3E] hover:bg-[#8B0029] text-white font-bold uppercase tracking-wider text-lg" data-testid="contact-submit-btn">
                  {submitting ? 'Sending...' : 'Get Free Inspection'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111827] text-white py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <img src={LOGO} alt={content.company_name} className="h-20 mb-6" />
              <p className="text-gray-400 leading-relaxed mb-6 max-w-md">
                {content.company_name} - {content.tagline}. Your trusted roofing and restoration experts in Houston and surrounding areas.
              </p>
              <div className="flex items-center space-x-2 text-[#BF0D3E] font-semibold">
                <Phone className="w-5 h-5" />
                <span>{content.main_phone}</span>
              </div>
            </div>

            <div>
              <h4 className="font-bold uppercase tracking-wider mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {['Services', 'Insurance Claims', 'Service Areas', 'Contact'].map((item) => (
                  <li key={item}>
                    <button onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))} className="text-gray-400 hover:text-white transition-colors">
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-bold uppercase tracking-wider mb-6">Contact</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>{content.main_phone}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>{content.email}</span>
                </li>
                <li className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>{content.address}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 text-center text-gray-500 text-sm">
            <p>© {new Date().getFullYear()} {content.company_name}. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Chat Widget Placeholder */}
      <div className="fixed bottom-6 right-6 z-50">
        <button className="w-14 h-14 bg-[#007AFF] rounded-full shadow-lg flex items-center justify-center hover:bg-[#0056b3] transition-colors" data-testid="chat-widget">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
